import { useEffect } from 'react';
import { useTranslation } from '@packages/utils';
import { useGetELCPlacesQuery } from 'redux/apiSlice/elcs.slice';
import { EContactsColumnKey, TELCPlacesUI } from 'data/types/elcs.types';
import useTable from 'components/Base/Table/hooks/useTable';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { TabBodyWrapper } from 'components/Base/Tabs';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import CardContent from 'components/Base/Layout/Content/CardContent';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import useGetData from './ELCPlacesTab.data';
import { TELCPlacesTabPRops } from './ELCPlacesTab.types';
import AddELCPlaces from './ELCPlacesActions/AddELCPlaces';

const ELCPlaces = ({ elcId }: TELCPlacesTabPRops) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.detail.elc-places-tab.list',
  });

  const isEditable = useCheckPermission('ELC_PLACES_EDIT');
  const { defaultColumns } = useGetData(elcId, isEditable);
  const {
    page,
    pageSize,
    setPageSize,
    sortColumn,
    selectedFilters,
    hasSelectedFilter,
    onFiltersReset,
    columns,
    setColumns,
    handleTableChange,
  } = useTable<TELCPlacesUI>({
    storagePath: ETableStoragePaths.ELC_PLACES,
    defaultColumns,
  });

  const { data, isFetching, isLoading } = useGetELCPlacesQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    elcId,
  });

  const { data: tagsFilters, isSuccess: tagsFiltersSuccess } =
    useGetTagsListQuery(ETagType.ORGANIZATION);

  useEffect(() => {
    if (tagsFiltersSuccess) {
      setColumns(p => {
        return p.map(item => {
          if (item.key === EContactsColumnKey.TAGS && tagsFiltersSuccess) {
            item.filters = tagsFilters;
            item.filterSearch = true;
          }
          return item;
        });
      });
    }
  }, [tagsFiltersSuccess, tagsFilters]);

  const { goOrganizationDetails } = useNavigateWithBackState();

  const navigateToOrgDetails = (record: TELCPlacesUI) => {
    return {
      onClick: () => goOrganizationDetails(record.organizationId, t('TITLE')),
    };
  };

  return (
    <CardContent tabContent contentPadding="8px 0px">
      <TabBodyWrapper
        isLoading={isLoading}
        emptyText={t('empty-view.EMPTY_TEXT')}
        emptyIcon={EIconNames.PARENT_OUTLINED}
      >
        <Table<TELCPlacesUI>
          onFiltersReset={onFiltersReset}
          hasFilters={hasSelectedFilter}
          columns={columns}
          {...(isEditable && {
            additionalActions: [<AddELCPlaces elcId={elcId} />],
          })}
          dataSource={data?.content}
          rowClassName="pointer"
          onRow={navigateToOrgDetails}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          rowKey={record => record.parentOrderId}
          currentPage={page}
          storagePath={ETableStoragePaths.ELC_PLACES}
        />
      </TabBodyWrapper>
    </CardContent>
  );
};

export default ELCPlaces;
